// EXTERNAL DEPENDENCIES
import React from 'react'
import { useDispatch } from 'react-redux'

// COMPONENTS
import PhotoUploadSection from '@/common/components/ui/profile/photo-upload-section'

// HELPERS
import { stringifyFptiProps, trackClick } from '@/utils'

// HOOKS
import usePhotoUploading from '@/hooks/ui'
import {
  useBusinessProfilePageContent,
  useBusinessProfileReduxState,
  useModularSellerViewPageContent,
  useSaveChanges,
} from '@/screens/business-profile-page/hooks'

// REDUX
import {
  updateEditedProfileDataValues,
  updateEditPageErrorMessage,
} from '@/redux/features'

// FPTI
import {
  BP_CHOOSE_EXISTING_COVER_PHOTO_FPTI,
  BP_CHOOSE_EXISTING_PROFILE_PHOTO_FPTI,
  BP_COVER_PHOTO_CROP_SCREEN_SHOWN_FPTI,
  BP_PHOTO_UPLOAD_ERROR_SHOWN_FPTI,
  BP_PHOTO_UPLOAD_SUCCESS_SHOWN_FPTI,
  BP_PROFILE_PHOTO_CROP_SCREEN_SHOWN_FPTI,
  BP_REMOVE_PROFILE_PHOTO_FPTI,
  BP_REMOVE_COVER_PHOTO_FPTI,
  BP_SAVE_COVER_PHOTO_FPTI,
  BP_SAVE_PROFILE_PHOTO_FPTI,
  BP_SELLER_VIEW_PROFILE_PHOTO_CLICK_FPTI,
  BP_SELLER_VIEW_COVER_PHOTO_CLICK_FPTI,
  BP_CHANGE_PROFILE_PHOTO_FPTI,
  BP_CHANGE_COVER_PHOTO_FPTI,
} from '../../constants/fpti'

// TYPES
import { PhotoTypes, ProfileData } from '@/types'

/**
 * Displays a screen allowing them to upload their profile photos and cover photos
 */
export default function ProfilePhotosSection({
  isOwnerPhoto,
}: Readonly<{
  isOwnerPhoto?: boolean
}>) {
  // INITIALIZATIONS
  const dispatch = useDispatch()

  // STATE
  const {
    uneditedFormattedData,
    editedProfileData: {
      values: { businessHandle, coverPhoto, profilePhoto },
    },
  } = useBusinessProfileReduxState()
  const isLoading = !businessHandle

  // INITIALIZE CONTENT
  const getBusinessProfileContent = useBusinessProfilePageContent()
  const getModularSellerViewContent = useModularSellerViewPageContent()
  const coverPhotoTitle = getBusinessProfileContent('coverPhotoTitle')
  const profilePhotoTitle = getBusinessProfileContent('profilePhotoTitle')
  const generalProfileUpdatedToast = getModularSellerViewContent(
    'generalProfileUpdatedToast',
  )
  const imageUpdateErrorToast = getModularSellerViewContent(
    'imageUpdateErrorToast',
  )

  const saveEdits = useSaveChanges({
    successMessage: generalProfileUpdatedToast,
    errorMessage: imageUpdateErrorToast,
  })

  const updateImageSrc = React.useCallback(
    (photoType: PhotoTypes, value: string, isFinishedValidating: boolean) => {
      dispatch(updateEditedProfileDataValues({ [photoType]: value }))

      if (isFinishedValidating) {
        // Note: this is to prevent the other photo from being overwritten
        // when the other photo is being deleted
        const { profilePhoto, coverPhoto } = uneditedFormattedData

        const data = {
          profilePhoto,
          coverPhoto,
          [photoType]: value,
        } as unknown as ProfileData

        const source = photoType === 'profilePhoto' ? 'profile' : 'cover'

        const successFpti = {
          ...BP_PHOTO_UPLOAD_SUCCESS_SHOWN_FPTI,
          source,
        }

        const errorFpti = {
          ...BP_PHOTO_UPLOAD_ERROR_SHOWN_FPTI,
          source,
        }

        saveEdits(data, { success: successFpti, error: errorFpti })
      }
    },
    [dispatch, saveEdits, uneditedFormattedData],
  )

  const profilePhotoUploadingProps = usePhotoUploading({
    photoType: 'profilePhoto',
    photoSrc: profilePhoto,
    updatePhotoValidity: (_photoType, _isValid) => {},
    updateImageSrc,
    isLoading,
    onDelete: () => trackClick(BP_REMOVE_PROFILE_PHOTO_FPTI),
  })

  const coverPhotoUploadingProps = usePhotoUploading({
    photoType: 'coverPhoto',
    photoSrc: coverPhoto,
    updatePhotoValidity: (_photoType, _isValid) => {},
    updateImageSrc,
    isLoading,
    onDelete: () => trackClick(BP_REMOVE_COVER_PHOTO_FPTI),
  })

  const setToastErrorMessage = (errorMessage: string) => {
    coverPhotoUploadingProps.setErrorMessage(errorMessage)
    dispatch(updateEditPageErrorMessage(errorMessage))
  }

  return (
    <div>
      <PhotoUploadSection
        {...coverPhotoUploadingProps}
        isDisplayPhoto
        isLoading={isLoading}
        photoType={'coverPhoto'}
        sheetTitle={coverPhotoTitle}
        errorMessage={null}
        fpti={{
          editFpti: stringifyFptiProps({
            ...BP_SELLER_VIEW_COVER_PHOTO_CLICK_FPTI,
            photoUploadedFlag: true,
          }),
          uploadFpti: stringifyFptiProps({
            ...BP_SELLER_VIEW_COVER_PHOTO_CLICK_FPTI,
            photoUploadedFlag: false,
          }),
          cropFpti: BP_COVER_PHOTO_CROP_SCREEN_SHOWN_FPTI,
          saveFpti: BP_SAVE_COVER_PHOTO_FPTI,
          selectNewPhotoFpti: BP_CHOOSE_EXISTING_COVER_PHOTO_FPTI,
          changeFpti: BP_CHANGE_COVER_PHOTO_FPTI,
        }}
        isOwnerPhoto={isOwnerPhoto}
        setErrorMessage={setToastErrorMessage}
      />
      <PhotoUploadSection
        {...profilePhotoUploadingProps}
        isLoading={isLoading}
        photoType={'profilePhoto'}
        sheetTitle={profilePhotoTitle}
        errorMessage={null}
        fpti={{
          editFpti: stringifyFptiProps({
            ...BP_SELLER_VIEW_PROFILE_PHOTO_CLICK_FPTI,
            photoUploadedFlag: true,
          }),
          uploadFpti: stringifyFptiProps({
            ...BP_SELLER_VIEW_PROFILE_PHOTO_CLICK_FPTI,
            photoUploadedFlag: false,
          }),
          changeFpti: BP_CHANGE_PROFILE_PHOTO_FPTI,
          saveFpti: BP_SAVE_PROFILE_PHOTO_FPTI,
          cropFpti: BP_PROFILE_PHOTO_CROP_SCREEN_SHOWN_FPTI,
          selectNewPhotoFpti: BP_CHOOSE_EXISTING_PROFILE_PHOTO_FPTI,
        }}
        setErrorMessage={setToastErrorMessage}
      />
    </div>
  )
}
